/**
 * Utility functions for route detection and pathname handling
 */

/**
 * List of all tools page routes
 */
export const TOOLS_ROUTES = [
  '/tools/job-flow-sankey',
  '/tools/viewed-jobs',
  '/tools/saved-jobs',
  '/tools/applied-jobs',
  '/tools/interviewing-jobs',
  '/tools/offer-jobs',
  '/tools/rejected-jobs',
  '/tools/hired-jobs',
  '/tools/hidden-jobs',
  '/tools/saved-searches',
] as const;

/**
 * Checks if a given pathname is a tools page route
 * Handles both localized and non-localized paths
 */
export function isToolsPage(pathname: string): boolean {
  // Remove locale prefix if present (e.g., /en/tools/viewed-jobs -> /tools/viewed-jobs)
  const normalizedPath = pathname.replace(/^\/(en|fi|sv)/, '') || '/';
  
  // Check if the path matches any tools route
  return TOOLS_ROUTES.some(route => {
    // Exact match
    if (normalizedPath === route) return true;
    // Match with trailing slash
    if (normalizedPath === `${route}/`) return true;
    return false;
  });
}

/**
 * Gets the current tool route from a pathname
 * Returns the normalized route (without locale) or null if not a tools page
 */
export function getCurrentToolRoute(pathname: string): string | null {
  if (!isToolsPage(pathname)) return null;
  
  const normalizedPath = pathname.replace(/^\/(en|fi|sv)/, '') || '/';
  
  for (const route of TOOLS_ROUTES) {
    if (normalizedPath === route || normalizedPath === `${route}/`) {
      return route;
    }
  }
  
  return null;
}

/**
 * Gets the viewMode for tools pages, normalizing 'map' to 'list' since map view
 * is not available on tools pages.
 * @param defaultMode - The default mode to use if no stored value or invalid value
 * @returns 'grid' | 'list' (never 'map')
 */
export function getToolsPageViewMode(defaultMode: 'grid' | 'list' = 'list'): 'grid' | 'list' {
  if (typeof window === 'undefined') return defaultMode;
  
  // Check both 'layout' and 'jobcrawls.viewMode' for backward compatibility
  const stored = localStorage.getItem('layout') || localStorage.getItem('jobcrawls.viewMode');
  
  // If stored value is 'map', normalize to 'list' since map view is not available on tools pages
  if (stored === 'map') {
    // Update localStorage to reflect the normalized value
    try {
      localStorage.setItem('layout', 'list');
      localStorage.setItem('jobcrawls.viewMode', 'list');
    } catch (err) {
      console.warn('[route-utils] Failed to update viewMode in localStorage:', err);
    }
    return 'list';
  }
  
  // Return stored value if it's valid for tools pages, otherwise return default
  if (stored === 'list' || stored === 'grid') {
    return stored as 'grid' | 'list';
  }
  
  return defaultMode;
}

